import { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowUpRight } from 'lucide-react';
import Eyebrow from './Eyebrow';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

const STATIC_CASES = [
  {
    industry: 'Financial Services',
    title: 'AI-Powered Credit Scoring Platform',
    desc: 'Replaced manual underwriting with an ML model processing 10,000+ applications/day at 94% accuracy — cutting decision time from 3 days to 60 seconds.',
    metrics: [{ val: '94%', label: 'Model Accuracy' }, { val: '60s', label: 'Decision Time' }, { val: '3.2×', label: 'Revenue Growth' }],
    gradient: 'linear-gradient(145deg, #2e1065 0%, #4c1d95 50%, #5E0ED7 100%)',
    tag: 'AI / ML',
  },
  {
    industry: 'Enterprise Software',
    title: 'Complete Product Redesign & Re-Platform',
    desc: 'Migrated a 10-year-old monolith to microservices while redesigning end-to-end — 60% faster load times, NPS from 22 to 71.',
    metrics: [{ val: '60%', label: 'Faster Load' }, { val: '+49', label: 'NPS Increase' }, { val: '40%', label: 'Churn Reduction' }],
    gradient: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
    tag: 'Platform / Design',
  },
  {
    industry: 'Healthcare',
    title: 'Patient Operations AI Assistant',
    desc: 'LLM-powered assistant handling scheduling, billing, and clinical documentation — saving 22 hours per staff member per week.',
    metrics: [{ val: '22h', label: 'Weekly Saved' }, { val: '85%', label: 'Query Resolution' }, { val: '$2.1M', label: 'Annual Savings' }],
    gradient: 'linear-gradient(145deg, #1e1b4b 0%, #3730a3 50%, #4f46e5 100%)',
    tag: 'Generative AI',
  },
];

function CaseCard({ c, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.08, duration: 0.6, ease }}
      whileHover={{ y: -4 }}
      className="cursor-default"
    >
      {/* Double-bezel — dark variant */}
      <div className="rounded-[1.75rem] p-[7px]" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div
          className="rounded-[1.375rem] p-8 md:p-10 text-white"
          style={{
            background: c.gradient,
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.08)',
          }}
        >
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-5">
                <span className="text-[10px] font-semibold tracking-[0.12em] uppercase px-3 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.12)' }}>
                  {c.tag}
                </span>
                <span className="text-xs text-white/40 font-medium">{c.industry}</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-4 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.02em' }}>
                {c.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed max-w-xl">{c.desc}</p>
            </div>
            <div className="flex flex-row md:flex-col gap-6 md:gap-7 md:text-right shrink-0">
              {c.metrics.map((m) => (
                <div key={m.label}>
                  <div className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{m.val}</div>
                  <div className="text-[10px] text-white/40 font-medium tracking-[0.1em] uppercase mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function CaseStudies() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [cases, setCases] = useState(STATIC_CASES);

  useEffect(() => {
    api.getCaseStudies()
      .then(d => {
        if (d?.length) setCases(d.map(c => ({
          ...c,
          desc: c.description,
          gradient: c.gradient || 'linear-gradient(135deg, #2e1065 0%, #5E0ED7 100%)',
          metrics: c.metrics || [],
        })));
      })
      .catch(() => {});
  }, []);

  return (
    <section id="studios" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-mesh" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
              <Eyebrow>Case Studies</Eyebrow>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.55, delay: 0.08, ease }}
              className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight max-w-xl leading-[1.08]"
              style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
            >
              Work That Speaks
            </motion.h2>
          </div>
          <motion.a
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.16, ease }}
            whileTap={{ scale: 0.97 }}
            href="#contact"
            className="flex items-center gap-1.5 text-sm font-semibold tracking-widest uppercase whitespace-nowrap"
            style={{ color: ACCENT }}
          >
            See All Work <ArrowUpRight size={14} />
          </motion.a>
        </div>

        <div className="flex flex-col gap-4">
          {cases.map((c, i) => (
            <CaseCard key={c.title} c={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
